import { useEffect, useState } from 'react';
import { getProfile, type ProfileEmployee } from '../api/profile';
import PushNotificationToggle from '../components/PushNotificationToggle';
import { Card, CardContent } from '../components/ui/card';
import LoadingState from '../components/ui/LoadingState';
import PageHeader from '../components/ui/PageHeader';
import { formatDate } from '@/lib/format';

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-2.5 text-sm border-b border-border last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right text-foreground">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {children}
      </CardContent>
    </Card>
  );
}

export default function Profile() {
  const [employee, setEmployee] = useState<ProfileEmployee | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getProfile().then((res) => {
      if (res.data) {
        setEmployee(res.data.employee);
        setEmail(res.data.user.email);
        setRole(res.data.user.role);
      } else if (res.error) {
        setError(res.error);
      }
      setLoading(false);
    });
  }, []);

  if (loading) return <LoadingState label="Loading profile…" rows={4} />;

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  const displayName = employee?.fullName ?? email ?? 'My profile';

  return (
    <div className="space-y-6">
      <PageHeader title="My profile" subtitle={displayName} />

      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-semibold text-primary">
          {(employee?.fullName ?? email ?? '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold">{employee?.fullName ?? email}</p>
          {employee?.position && (
            <p className="text-sm text-muted-foreground">{employee.position.name}</p>
          )}
          {role && (
            <p className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {role.replace(/_/g, ' ')}
            </p>
          )}
        </div>
      </div>

      <Section title="Contact">
        <Row label="Email" value={email} />
        <Row label="Phone" value={employee?.phoneNumber} />
      </Section>

      <Card>
        <CardContent className="p-4">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Notifications
          </h3>
          <PushNotificationToggle />
        </CardContent>
      </Card>

      <Section title="Employment">
        <Row label="Employee No." value={employee?.employeeNumber} />
        <Row label="Department" value={employee?.department?.name} />
        <Row label="Position" value={employee?.position?.name} />
        <Row label="Manager" value={employee?.manager?.fullName} />
        <Row label="Employment type" value={employee?.employmentType?.replace(/_/g, ' ')} />
        <Row label="Status" value={employee?.employmentStatus?.replace(/_/g, ' ')} />
        <Row label="Work site" value={employee?.workSite?.name} />
        <Row label="Company entity" value={employee?.companyEntity?.name} />
      </Section>

      <Section title="Dates">
        <Row label="Joined" value={employee?.joiningDate ? formatDate(employee.joiningDate) : null} />
        <Row label="Confirmed" value={employee?.confirmationDate ? formatDate(employee.confirmationDate) : null} />
      </Section>
    </div>
  );
}
